import * as THREE from 'three';
import { CFRenderer, LightingSystem } from '../engine/Renderer';
import { InputManager } from '../engine/Input';
import { CameraController } from '../engine/Camera';
import { PhysicsEngine, PlayerPhysicsState } from '../engine/Physics';
import { AudioManager } from '../engine/Audio';
import { WeaponSystem } from './WeaponSystem';
import { PlayerRenderer, ViewmodelRenderer } from './PlayerRenderer';
import { GameServer, ServerPlayer } from './GameServer';
import {
  NetworkManager, ClientPrediction, InterpolationBuffer, SnapshotBuffer,
} from '../network/NetworkManager';
import { PlayerInput, PlayerSnapshot, GameMode, GameEvent } from '../shared/types';
import {
  TICK_RATE, FIXED_DT, EYE_HEIGHT, CROUCH_EYE_HEIGHT,
  PLAYER_HEIGHT, CROUCH_HEIGHT,
} from '../shared/constants';
import { lerp, interpolatePositions, generateId } from '../shared/utils';

export interface GameClientState {
  running: boolean;
  isHost: boolean;
  connected: boolean;
  playerId: string;
  playerName: string;
  health: number;
  armor: number;
  alive: boolean;
  ammo: number;
  reserveAmmo: number;
  currentWeapon: string;
  kills: number;
  deaths: number;
  assists: number;
  score: number;
  ping: number;
  fps: number;
  matchState: string;
  timeRemaining: number;
  events: GameEvent[];
  players: PlayerSnapshot[];
  roomId: string;
}

type StateCallback = (state: GameClientState) => void;

export class GameClient {
  public renderer: CFRenderer;
  public lighting: LightingSystem;
  public input: InputManager;
  public camera: CameraController;
  public physics: PhysicsEngine;
  public audio: AudioManager;
  public playerRenderer: PlayerRenderer;
  public viewmodel: ViewmodelRenderer;
  public weaponSystem: WeaponSystem;
  public network: NetworkManager;
  public prediction: ClientPrediction;
  public interpolation: InterpolationBuffer;
  public server: GameServer | null = null;

  public localPlayerId = '';
  public playerName = 'Player';
  public roomId = '';
  public isHost = false;
  public running = false;
  public stateCallbacks: StateCallback[] = [];

  private localState: PlayerPhysicsState | null = null;
  private remotePlayers = new Map<string, {
    state: PlayerPhysicsState;
    target: PlayerSnapshot;
    interpolated: boolean;
  }>();
  private frameTime = 0;
  private lastFrameTime = 0;
  private accumulator = 0;
  private tickRate = TICK_RATE;
  private fixedDt = FIXED_DT;
  private inputTimer: ReturnType<typeof setInterval> | null = null;
  private currentWeaponSlot: import('../shared/types').WeaponSlot = 'primary';
  private events: GameEvent[] = [];
  private killFeedItems: { text: string; time: number; id: string }[] = [];
  private mapGroup: THREE.Group;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new CFRenderer(canvas);
    this.lighting = new LightingSystem(this.renderer.scene);
    this.input = new InputManager(canvas);
    this.camera = new CameraController(this.renderer.camera);
    this.physics = new PhysicsEngine();
    this.audio = new AudioManager();
    this.playerRenderer = new PlayerRenderer(this.renderer.scene);
    this.viewmodel = new ViewmodelRenderer(this.renderer.camera);
    this.weaponSystem = new WeaponSystem();
    this.network = new NetworkManager('');
    this.prediction = new ClientPrediction();
    this.interpolation = new InterpolationBuffer();

    this.mapGroup = new THREE.Group();
    this.mapGroup.name = 'map';
    this.renderer.scene.add(this.mapGroup);

    this.buildDefaultMap();
    this.renderer.camera.add(this.viewmodel.group);

    this.lastFrameTime = performance.now();
    requestAnimationFrame(this.frame.bind(this));
  }

  onStateChange(callback: StateCallback): void {
    this.stateCallbacks.push(callback);
  }

  private emitState(): void {
    const state = this.getClientState();
    for (const cb of this.stateCallbacks) {
      cb(state);
    }
  }

  private getClientState(): GameClientState {
    return {
      running: this.running,
      isHost: this.isHost,
      connected: this.network.connected,
      playerId: this.localPlayerId,
      playerName: this.playerName,
      health: Math.round(this.localState?.health ?? 100),
      armor: Math.round(this.localState?.armor ?? 0),
      alive: this.localState?.alive ?? true,
      ammo: this.weaponSystem.getState().ammo,
      reserveAmmo: this.weaponSystem.getState().reserve,
      currentWeapon: this.weaponSystem.getCurrentWeapon().name,
      kills: this.server?.players.get(this.localPlayerId)?.kills ?? 0,
      deaths: this.server?.players.get(this.localPlayerId)?.deaths ?? 0,
      assists: this.server?.players.get(this.localPlayerId)?.assists ?? 0,
      score: this.server?.state.scores[this.localPlayerId] ?? 0,
      ping: Math.round(this.network.ping),
      fps: this.renderer.fps,
      matchState: this.server?.state.matchState ?? 'waiting',
      timeRemaining: this.server?.state.timeRemaining ?? 0,
      events: this.events.slice(-10),
      players: this.server?.getSnapshot() ?? [],
      roomId: this.roomId,
    };
  }

  startAsHost(playerName: string, gameMode: GameMode = 'ffa'): void {
    this.playerName = playerName;
    this.isHost = true;
    this.running = true;

    this.server = new GameServer({
      gameMode,
      mapId: 'arena',
      maxPlayers: 16,
      timeLimit: 600,
      scoreLimit: 30,
    });

    this.localPlayerId = generateId();
    this.roomId = generateId();

    const spawnX = (Math.random() - 0.5) * 30;
    const spawnZ = (Math.random() - 0.5) * 30;
    this.localState = this.physics.createPlayerState(spawnX, 0, spawnZ);
    this.server.addPlayer(this.localPlayerId, playerName);

    this.playerRenderer.createPlayer(this.localPlayerId, playerName, true);
    this.server.startMatch();

    this.input.requestLock();
    this.startInputLoop();
    this.emitState();
  }

  async joinAsClient(serverUrl: string, playerName: string, roomId: string): Promise<void> {
    this.playerName = playerName;
    this.isHost = false;
    this.roomId = roomId;
    this.network = new NetworkManager(serverUrl);

    this.network.on('welcome', (msg) => {
      this.localPlayerId = msg.playerId as string;
      this.playerName = msg.name as string || playerName;
      this.network.connected = true;
      this.localState = this.physics.createPlayerState(0, 0, 0);
      this.playerRenderer.createPlayer(this.localPlayerId, this.playerName, true);
      this.emitState();
    });

    this.network.on('snapshot', (msg) => {
      this.handleServerSnapshot(msg);
    });

    this.network.on('event', (msg) => {
      this.events.push({
        id: msg.eventId as string,
        type: (msg.eventType as GameEvent['type']) || 'system',
        text: msg.text as string,
        timestamp: Date.now(),
      });
      this.addKillFeedItem(msg.text as string);
    });

    try {
      await this.network.connect();
      this.network.send({ type: 'register', room: roomId, peerId: playerName, role: 'client' });
      this.running = true;
      this.input.requestLock();
      this.startInputLoop();
    } catch (e) {
      console.error('Failed to connect:', e);
    }
  }

  private startInputLoop(): void {
    if (this.inputTimer) clearInterval(this.inputTimer);
    this.inputTimer = setInterval(() => {
      if (!this.running || !this.localState) return;
      const input = this.input.collectInput(this.currentWeaponSlot);

      if (this.isHost && this.server) {
        this.server.processInput(this.localPlayerId, input);
      } else {
        this.prediction.recordInput(input, {
          x: this.localState.position.x,
          y: this.localState.position.y,
          z: this.localState.position.z,
          vx: this.localState.velocity.x,
          vy: this.localState.velocity.y,
          vz: this.localState.velocity.z,
        });
        this.network.sendInput(input);
      }
    }, 1000 / this.tickRate);
  }

  private handleServerSnapshot(msg: Record<string, unknown>): void {
    const players = msg.players as PlayerSnapshot[] | undefined;
    if (!players) return;

    for (const remote of players) {
      if (remote.id === this.localPlayerId) {
        const corrected = this.prediction.reconcile(
          { x: remote.x, y: remote.y, z: remote.z },
          remote.id ? 0 : 0,
          this.localState ? {
            x: this.localState.position.x,
            y: this.localState.position.y,
            z: this.localState.position.z,
            vx: this.localState.velocity.x,
            vy: this.localState.velocity.y,
            vz: this.localState.velocity.z,
          } : { x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0 }
        );
        if (corrected && this.localState) {
          this.localState.position.x = corrected.x;
          this.localState.position.y = corrected.y;
          this.localState.position.z = corrected.z;
        }
        if (this.localState) {
          this.localState.health = remote.health;
          this.localState.alive = remote.alive;
          this.localState.crouching = remote.crouching;
        }
        continue;
      }

      if (!this.remotePlayers.has(remote.id)) {
        this.playerRenderer.createPlayer(remote.id, remote.name, false);
        this.remotePlayers.set(remote.id, {
          state: this.physics.createPlayerState(remote.x, remote.y, remote.z),
          target: remote,
          interpolated: false,
        });
      }
      this.remotePlayers.get(remote.id)!.target = remote;
    }

    for (const [id, rp] of this.remotePlayers) {
      if (!players.find(p => p.id === id)) {
        this.playerRenderer.removePlayer(id);
        this.remotePlayers.delete(id);
      }
    }

    this.emitState();
  }

  private frame(now: number): void {
    const dt = Math.min((now - this.lastFrameTime) / 1000, 0.1);
    this.lastFrameTime = now;
    this.frameTime = dt;

    this.renderer.updateFps(dt);

    if (this.isHost && this.server && this.localState) {
      this.accumulator += dt;
      while (this.accumulator >= this.fixedDt) {
        this.server.tick(this.fixedDt);
        this.accumulator -= this.fixedDt;
      }

      const localPlayer = this.server.players.get(this.localPlayerId);
      if (localPlayer) {
        this.localState = localPlayer.state;
      }
    }

    this.processInput(dt);
    this.weaponSystem.update(dt);

    if (this.localState) {
      const eye = this.localState.crouching ? CROUCH_EYE_HEIGHT : EYE_HEIGHT;
      const target = new THREE.Vector3(
        this.localState.position.x,
        this.localState.position.y + eye,
        this.localState.position.z
      );
      this.camera.update(dt, target, this.input.yaw, this.input.pitch);

      const hSpeed = Math.sqrt(
        this.localState.velocity.x ** 2 + this.localState.velocity.z ** 2
      );
      this.viewmodel.update(dt, hSpeed > 0.5, hSpeed);

      this.audio.updateListenerPosition(
        this.localState.position.x,
        this.localState.position.y + eye,
        this.localState.position.z,
        this.input.yaw,
        this.input.pitch
      );
    }

    this.updateRemotePlayers(dt);
    this.updateKillFeed(dt);
    this.renderer.render();
    this.input.endFrame();
    this.emitState();

    requestAnimationFrame(this.frame.bind(this));
  }

  private processInput(dt: number): void {
    if (!this.localState) return;

    if (this.input.wasKeyJustPressed('Digit1')) this.switchWeapon('primary');
    if (this.input.wasKeyJustPressed('Digit2')) this.switchWeapon('secondary');
    if (this.input.wasKeyJustPressed('Digit3')) this.switchWeapon('melee');
    if (this.input.wasKeyJustPressed('KeyR')) this.weaponSystem.startReload();
    if (this.input.wasKeyJustPressed('Escape')) {
      this.input.exitLock();
    }
    if (this.input.isMouse(0) && this.input.locked) {
      this.weaponSystem.fire();
      this.camera.addShake(0.3, 0.1);
    }
  }

  private updateRemotePlayers(dt: number): void {
    for (const [id, rp] of this.remotePlayers) {
      const target = rp.target;
      const state = rp.state;

      state.position.x = lerp(state.position.x, target.x, dt * 15);
      state.position.y = lerp(state.position.y, target.y, dt * 15);
      state.position.z = lerp(state.position.z, target.z, dt * 15);
      state.velocity = { x: target.vx, y: target.vy, z: target.vz };
      state.yaw = target.yaw;
      state.pitch = target.pitch;
      state.health = target.health;
      state.crouching = target.crouching;

      this.playerRenderer.updatePlayer(id, state, false, this.renderer.camera);
    }
  }

  private addKillFeedItem(text: string): void {
    this.killFeedItems.push({
      text,
      time: 5,
      id: generateId(),
    });
    if (this.killFeedItems.length > 6) {
      this.killFeedItems.shift();
    }
  }

  private updateKillFeed(dt: number): void {
    for (let i = this.killFeedItems.length - 1; i >= 0; i--) {
      this.killFeedItems[i].time -= dt;
      if (this.killFeedItems[i].time <= 0) {
        this.killFeedItems.splice(i, 1);
      }
    }
  }

  private switchWeapon(slot: import('../shared/types').WeaponSlot): void {
    this.weaponSystem.switchWeapon(slot);
    this.currentWeaponSlot = slot;
  }

  private buildDefaultMap(): void {
    const floorGeo = new THREE.PlaneGeometry(100, 100, 100, 100);
    const floorMat = new THREE.MeshStandardMaterial({
      color: 0x2a2e3d,
      roughness: 0.85,
      metalness: 0.05,
    });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    this.mapGroup.add(floor);

    const grid = new THREE.GridHelper(100, 100, 0x1a1d26, 0x2a2e3d);
    grid.position.y = 0.01;
    this.mapGroup.add(grid);

    const obstacles = [
      { x: -12, z: -10, w: 4, d: 10, h: 4, color: 0xff1744 },
      { x: 12, z: 10, w: 5, d: 8, h: 3.5, color: 0x00e5ff },
      { x: -3, z: 14, w: 10, d: 3, h: 3, color: 0xffc400 },
      { x: 16, z: -12, w: 7, d: 4, h: 5, color: 0x536dfe },
      { x: -18, z: 5, w: 6, d: 6, h: 3.5, color: 0x00e676 },
      { x: 5, z: -18, w: 8, d: 4, h: 4, color: 0x7c4dff },
      { x: 0, z: 0, w: 3, d: 3, h: 2.5, color: 0xff4081 },
    ];

    for (const obs of obstacles) {
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(obs.w, obs.h, obs.d),
        new THREE.MeshStandardMaterial({ color: obs.color, roughness: 0.6, metalness: 0.1 })
      );
      mesh.position.set(obs.x, obs.h / 2, obs.z);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.mapGroup.add(mesh);

      this.physics.obstacles.push({
        position: { x: obs.x, y: 0, z: obs.z },
        size: { x: obs.w, y: obs.h, z: obs.d },
        color: obs.color,
        physics: true,
      });
    }

    const wallMat = new THREE.MeshStandardMaterial({
      color: 0x1a1d26,
      roughness: 0.9,
      metalness: 0.02,
    });

    const walls = [
      { pos: [0, 1.5, -50] as [number, number, number], size: [100, 3, 1] as [number, number, number] },
      { pos: [0, 1.5, 50] as [number, number, number], size: [100, 3, 1] as [number, number, number] },
      { pos: [-50, 1.5, 0] as [number, number, number], size: [1, 3, 100] as [number, number, number] },
      { pos: [50, 1.5, 0] as [number, number, number], size: [1, 3, 100] as [number, number, number] },
    ];

    for (const wall of walls) {
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(...wall.size),
        wallMat
      );
      mesh.position.set(...wall.pos);
      mesh.receiveShadow = true;
      this.mapGroup.add(mesh);
    }
  }

  destroy(): void {
    this.running = false;
    if (this.inputTimer) clearInterval(this.inputTimer);
    this.network.dispose();
    this.audio.dispose();
    this.playerRenderer.dispose();
    this.viewmodel.dispose();
    this.input.dispose();
    this.renderer.dispose();
  }
}
